import { Menu, X } from 'lucide-react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { AccessibilityToggle } from '@/components/accessibility-toggle'
import { useGetInTouchOverlay } from '@/components/get-in-touch-overlay'
import { GetInTouchTrigger } from '@/components/get-in-touch-trigger'
import { MobileNavOverlay } from '@/components/mobile-nav-overlay'
import { MorningLogoLink } from '@/components/morning-logo-link'
import { contactSubmitButtonClassName } from '@/components/contact-form-styles'
import { ThemeToggle } from '@/components/theme-toggle'
import {
  stickyHeaderBarRestingClassName,
  stickyHeaderBarRestingStyle,
  stickyHeaderReachTargetId,
  stickyHeaderReachedScheme,
  stickyHeaderScrolledScheme,
  type StickyHeaderScrolledScheme,
  useElementPaddingTop,
  useHeaderScrollPhase,
} from '@/hooks/use-has-scrolled'
import { cn } from '@/lib/utils'

const heroPrimaryClass = contactSubmitButtonClassName

const primaryNavLinks = [
  { href: '/#services', label: 'Services' },
  { href: '/#design-process', label: 'Process' },
  { href: '/#faq', label: 'FAQ' },
  { href: '/#contact', label: 'Contact' },
] as const

const headerIconButtonClass =
  'relative inline-flex size-11 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-zinc-200 shadow-sm backdrop-blur-sm transition-all outline-none select-none hover:bg-white/15 hover:text-white focus-visible:ring-3 focus-visible:ring-white/25 sm:size-10'

type SiteHeaderNavLinkProps = {
  href: string
  className?: string
  onClick?: () => void
  children: React.ReactNode
}

function SiteHeaderNavLink({
  href,
  className,
  onClick,
  children,
}: SiteHeaderNavLinkProps) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        'rounded-md px-3 py-1.5 tracking-[0.08em] text-current opacity-100 transition-colors',
        className,
      )}
    >
      {children}
    </a>
  )
}

type HeaderGetInTouchTriggerProps = {
  showBackground: boolean
  activeScheme: StickyHeaderScrolledScheme
  className?: string
}

function HeaderGetInTouchTrigger({
  showBackground,
  activeScheme,
  className,
}: HeaderGetInTouchTriggerProps) {
  return (
    <GetInTouchTrigger
      className={cn(
        showBackground ? activeScheme.ctaClassName : heroPrimaryClass,
        className,
      )}
    />
  )
}

function TouchTargetExpand() {
  return (
    <span
      className="pointer-fine:hidden absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2"
      aria-hidden="true"
    />
  )
}

export type SiteHeaderNavProps = {
  barClassName: string
  navClassName: string
  linkClassName: string
  /** First scrolled surface (default greyblue). */
  scrolledScheme?: StickyHeaderScrolledScheme
  /** Surface once the bar reaches the logo carousel (default ember). */
  reachedScheme?: StickyHeaderScrolledScheme
  /** DOM id of the logo carousel — defaults to `technology-logos`. */
  reachTargetId?: string
  /**
   * Sub pages: keep the solid scrolled chrome from the first paint
   * (no transparent resting / floating inset state).
   */
  forceScrolled?: boolean
  className?: string
}

export function SiteHeaderNav({
  barClassName,
  navClassName,
  linkClassName,
  scrolledScheme = stickyHeaderScrolledScheme,
  reachedScheme = stickyHeaderReachedScheme,
  reachTargetId = stickyHeaderReachTargetId,
  forceScrolled = false,
  className,
}: SiteHeaderNavProps) {
  const insetMeasureRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const insetPx = useElementPaddingTop(insetMeasureRef)
  const scrollPhase = useHeaderScrollPhase(insetPx, {
    reachTargetId: forceScrolled ? undefined : reachTargetId,
    barRef,
  })
  const { registerMobileContactOpener } = useGetInTouchOverlay()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileMenuRendered, setMobileMenuRendered] = useState(false)
  const [mobileContactOpen, setMobileContactOpen] = useState(false)
  const mobileMenuCloseRef = useRef<(() => void) | null>(null)
  const mobileMenuId = useId()
  // While the mobile menu is up, keep a flush ember bar above the overlay.
  const menuOwnsChrome = mobileMenuRendered
  const atTop = forceScrolled || menuOwnsChrome ? false : scrollPhase.atTop
  const showBackground =
    forceScrolled || menuOwnsChrome ? true : scrollPhase.showBackground
  const reachedSurface =
    forceScrolled || menuOwnsChrome ? true : scrollPhase.reachedSurface
  const activeScheme = reachedSurface ? reachedScheme : scrolledScheme

  const openMobileMenu = useCallback((options?: { contact?: boolean }) => {
    setMobileMenuRendered(true)
    setMobileMenuOpen(true)
    setMobileContactOpen(Boolean(options?.contact))
  }, [])

  const handleMobileMenuClosed = useCallback(() => {
    setMobileMenuRendered(false)
    setMobileMenuOpen(false)
    setMobileContactOpen(false)
    requestAnimationFrame(() => {
      menuButtonRef.current?.focus()
    })
  }, [])

  useEffect(() => {
    registerMobileContactOpener(() => {
      openMobileMenu({ contact: true })
    })
    return () => registerMobileContactOpener(null)
  }, [openMobileMenu, registerMobileContactOpener])

  useEffect(() => {
    if (!mobileMenuOpen) return

    const media = window.matchMedia('(min-width: 1024px)')
    const onViewportChange = () => {
      if (media.matches) {
        setMobileMenuRendered(false)
        setMobileMenuOpen(false)
        setMobileContactOpen(false)
      }
    }

    media.addEventListener('change', onViewportChange)
    return () => media.removeEventListener('change', onViewportChange)
  }, [mobileMenuOpen])

  const desktopNavLinkClassName = showBackground
    ? activeScheme.navLinkClassName
    : cn('hover:bg-white/10 hover:text-white', linkClassName)

  const mobileMenuButtonClassName = showBackground
    ? activeScheme.utilityButtonClassName
    : headerIconButtonClass

  return (
    <div className={className}>
      <div
        ref={insetMeasureRef}
        className="pointer-events-none absolute size-0 overflow-hidden pt-4 sm:pt-5"
        aria-hidden
      />

      {mobileMenuRendered ? (
        <MobileNavOverlay
          id={mobileMenuId}
          links={primaryNavLinks}
          onClosed={handleMobileMenuClosed}
          closeRef={mobileMenuCloseRef}
          contactOpen={mobileContactOpen}
          onContactOpenChange={setMobileContactOpen}
        />
      ) : null}

      <div
        className={cn(
          'relative z-50',
          // Menu owns the chrome — snap flush so overlay offset measuring isn’t
          // stuck on the mid-transition padded position.
          menuOwnsChrome
            ? 'pt-0'
            : cn(
                'transition-[padding-top] duration-300 ease-out',
                atTop ? 'pt-4 sm:pt-5' : 'pt-0',
              ),
        )}
      >
        <div
          ref={barRef}
          data-site-header-bar=""
          className={cn(
            barClassName,
            !showBackground && stickyHeaderBarRestingClassName,
            showBackground && activeScheme.barClassName,
          )}
          style={
            showBackground ? activeScheme.barStyle : stickyHeaderBarRestingStyle
          }
          role="navigation"
          aria-label="Main"
        >
          <div className="mx-auto w-full max-w-6xl px-6">
            <div className="flex w-full items-center justify-between gap-3 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-4">
              <MorningLogoLink
                className="min-w-0 shrink-0 justify-self-start lg:col-start-1"
                scrolled={showBackground}
                scrolledScheme={activeScheme}
              />
              <nav
                className={cn(
                  'hidden items-center justify-center gap-3 font-heading-outfit text-sm font-medium uppercase lg:col-start-2 lg:flex lg:gap-5',
                  showBackground ? activeScheme.navClassName : navClassName,
                )}
                aria-label="Primary"
              >
                {primaryNavLinks.map((link) => (
                  <SiteHeaderNavLink
                    key={link.href}
                    href={link.href}
                    className={desktopNavLinkClassName}
                  >
                    {link.label}
                  </SiteHeaderNavLink>
                ))}
              </nav>
              <div className="flex shrink-0 items-center justify-end gap-2 lg:col-start-3 lg:justify-self-end">
                <button
                  ref={menuButtonRef}
                  type="button"
                  className={cn(mobileMenuButtonClassName, 'lg:hidden')}
                  aria-expanded={mobileMenuOpen}
                  aria-controls={mobileMenuId}
                  aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  onClick={() => {
                    if (mobileMenuOpen) {
                      mobileMenuCloseRef.current?.()
                      return
                    }
                    openMobileMenu()
                  }}
                >
                  <TouchTargetExpand />
                  {mobileMenuOpen ? (
                    <X className="size-5 sm:size-4" aria-hidden />
                  ) : (
                    <Menu className="size-5 sm:size-4" aria-hidden />
                  )}
                </button>
                <AccessibilityToggle
                  className="hidden lg:flex"
                  triggerClassName={cn(
                    showBackground && activeScheme.utilityButtonClassName,
                  )}
                >
                  <ThemeToggle
                    variant={showBackground ? 'utility' : 'icon-muted'}
                    className="hidden lg:inline-flex"
                  />
                  <HeaderGetInTouchTrigger
                    showBackground={showBackground}
                    activeScheme={activeScheme}
                  />
                </AccessibilityToggle>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
